import {
  BudgetArbitrationRow,
  BudgetConsolidationAccountRow,
  BudgetConsolidationActions,
  BudgetConsolidationCards,
  BudgetConsolidationContext,
  BudgetConsolidationCostCenterRow,
  BudgetConsolidationWorkbenchModel,
  BudgetControlAreaSummary,
} from './budgetconsolidation.models';

const n = (value: any, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;

const s = (value: any, fallback = '') =>
  value === null || value === undefined ? fallback : String(value);

const b = (value: any) =>
  value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';

export const normalizeContext = (raw: any): BudgetConsolidationContext => ({
  planningcycleid: n(raw?.planningcycleid ?? raw?.pci),
  planningcyclecode: s(raw?.planningcyclecode ?? raw?.pcc),
  planningcyclename: s(raw?.planningcyclename ?? raw?.pcn),
  budgetperiodid: n(raw?.budgetperiodid ?? raw?.bpi),
  budgetperiodcode: s(raw?.budgetperiodcode ?? raw?.bpc),
  budgetperiodname: s(raw?.budgetperiodname ?? raw?.bpn),
  phasecode: s(raw?.phasecode),
  statecode: s(raw?.statecode),
  statename: s(raw?.statename),
  stamp: s(raw?.stamp ?? raw?.stp),
});

export const normalizeCards = (raw: any): BudgetConsolidationCards => ({
  activitybudget: n(raw?.activitybudget),
  directbudget: n(raw?.directbudget),
  investmentbudget: n(raw?.investmentbudget),
  totalbudget: n(raw?.totalbudget),
  approvedcapacity: n(raw?.approvedcapacity ?? raw?.ceilingamount),
  availablecapacity: n(raw?.availablecapacity ?? raw?.availableamount),
  variance: n(raw?.variance),
  costcenters: n(raw?.costcenters),
  accounts: n(raw?.accounts),
  unmappedamount: n(raw?.unmappedamount),
  unmappedlines: n(raw?.unmappedlines),
  pendingarbitrations: n(raw?.pendingarbitrations),
  approvedarbitrations: n(raw?.approvedarbitrations),
  rejectedarbitrations: n(raw?.rejectedarbitrations),
});

export const normalizeControlArea = (raw: any): BudgetControlAreaSummary => ({
  controlareaid: n(raw?.controlareaid),
  controlareacode: s(raw?.controlareacode),
  controlareaname: s(raw?.controlareaname),
  ceilingamount: n(raw?.ceilingamount),
  requestedamount: n(raw?.requestedamount),
  approvedamount: n(raw?.approvedamount),
  availableamount: n(raw?.availableamount),
  utilisationpercent: n(raw?.utilisationpercent),
  warningpercent: n(raw?.warningpercent),
  controlstatus: s(raw?.controlstatus),
});

export const normalizeAccountRow = (raw: any, index = 0): BudgetConsolidationAccountRow => ({
  id: raw?.id ?? raw?.accountid ?? raw?.accountcode ?? `account-${index}`,
  accountid: n(raw?.accountid),
  accountcode: s(raw?.accountcode),
  accountname: s(raw?.accountname),
  activitybudget: n(raw?.activitybudget),
  directbudget: n(raw?.directbudget),
  investmentbudget: n(raw?.investmentbudget),
  totalbudget: n(raw?.totalbudget),
  approvedamount: n(raw?.approvedamount),
  variance: n(raw?.variance),
  status: s(raw?.status),
  raw,
});

export const normalizeCostCenterRow = (raw: any, index = 0): BudgetConsolidationCostCenterRow => ({
  id: raw?.id ?? raw?.costcenterid ?? raw?.costcentercode ?? `costcenter-${index}`,
  costcenterid: n(raw?.costcenterid),
  costcentercode: s(raw?.costcentercode),
  costcentername: s(raw?.costcentername),
  activitybudget: n(raw?.activitybudget),
  directbudget: n(raw?.directbudget),
  investmentbudget: n(raw?.investmentbudget),
  totalbudget: n(raw?.totalbudget),
  approvedamount: n(raw?.approvedamount),
  variance: n(raw?.variance),
  status: s(raw?.status),
  raw,
});

export const normalizeArbitrationRow = (raw: any, index = 0): BudgetArbitrationRow => {
  const requestedamount = n(raw?.requestedamount ?? raw?.originalamount);
  const recommendedamount = n(raw?.recommendedamount, requestedamount);
  const arbitratedamount = n(raw?.arbitratedamount, recommendedamount);
  const varianceamount = arbitratedamount - requestedamount;
  const variancepercent = requestedamount === 0 ? 0 : varianceamount / requestedamount * 100;

  return {
    id: raw?.id ?? raw?.arbitrationid ?? `${s(raw?.sourceclasscode)}-${n(raw?.sourceid)}-${index}`,
    arbitrationid: raw?.arbitrationid == null ? null : n(raw.arbitrationid),
    arbitrationix: s(raw?.arbitrationix),
    planningcycleid: n(raw?.planningcycleid ?? raw?.pci),
    sourceclasscode: s(raw?.sourceclasscode),
    sourceclassname: s(raw?.sourceclassname),
    sourceid: n(raw?.sourceid),
    sourcecode: s(raw?.sourcecode),
    sourcename: s(raw?.sourcename),
    costcenterid: raw?.costcenterid == null ? null : n(raw.costcenterid),
    costcentercode: s(raw?.costcentercode),
    costcentername: s(raw?.costcentername),
    accountid: raw?.accountid == null ? null : n(raw.accountid),
    accountcode: s(raw?.accountcode),
    accountname: s(raw?.accountname),
    requestedamount,
    recommendedamount,
    arbitratedamount,
    varianceamount,
    variancepercent,
    decisioncode: s(raw?.decisioncode, 'PENDING'),
    reason: s(raw?.reason),
    remarks: s(raw?.remarks),
    statecode: s(raw?.statecode),
    statename: s(raw?.statename),
    stamp: s(raw?.stamp ?? raw?.stp),
    _state: 'clean',
    __dirty: false,
    __error: '',
    raw,
  };
};

export const normalizeActions = (raw: any): BudgetConsolidationActions => ({
  canRefresh: b(raw?.canRefresh ?? true),
  canArbitrate: b(raw?.canArbitrate),
  canSaveArbitration: b(raw?.canSaveArbitration),
  canComplete: b(raw?.canComplete),
  canReturn: b(raw?.canReturn),
  canExit: b(raw?.canExit),
  nextpage: raw?.nextpage ?? null,
});

export const normalizeWorkbench = (raw: any): BudgetConsolidationWorkbenchModel => {
  const source = raw?.sd ?? raw ?? {};
  return {
    context: normalizeContext(source.context),
    cards: normalizeCards(source.cards ?? source.summary),
    controlareas: Array.isArray(source.controlareas)
      ? source.controlareas.map(normalizeControlArea)
      : [],
    accounts: Array.isArray(source.accounts)
      ? source.accounts.map(normalizeAccountRow)
      : [],
    costcenters: Array.isArray(source.costcenters)
      ? source.costcenters.map(normalizeCostCenterRow)
      : [],
    arbitrations: Array.isArray(source.arbitrations)
      ? source.arbitrations.map(normalizeArbitrationRow)
      : [],
    actions: normalizeActions(source.actions),
  };
};
